import React, { useState, useEffect } from "react";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

const CartPage = () => {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const userId = localStorage.getItem("userId") || "10";
  const deviceId = localStorage.getItem("deviceId") || "web";

  // Hide navbar
  useEffect(() => {
    const navbar = document.querySelector("nav");
    if (navbar) navbar.style.display = "none";
    return () => {
      if (navbar) navbar.style.display = "";
    };
  }, []);

  // Fetch cart and inventory
  const fetchCartAndInventory = async () => {
    try {
      const [cartRes, invRes] = await Promise.all([
        axios.get(`https://dharajwebbackend.concentics.com/api/get_cartdata/${deviceId}/${userId}`),
        axios.get(`https://dharajwebbackend.concentics.com/api/get_inventory`)
      ]);

      if (cartRes.data?.status === "success" && invRes.data?.status === "success") {
        const inventoryList = invRes.data.inventory;

        const items = cartRes.data.cart.map(item => {
          let img = item.cartImage || '';
          if (img && !img.startsWith('http')) img = `https://dharajwebbackend.concentics.com/productimage/${img}`;
          if (!img) img = 'https://th.bing.com/th/id/OIP.cmTcUqX-2WFqJofdiwQLBQHaHa?w=196';

          const variant = inventoryList.find(inv => inv.id === item.variantId || inv.pid === item.pid);

          return {
            id: item.cart_id || item.id,
            name: item.product_name || 'Unknown Product',
            image: img,
            price: Number(item.cartprice) || 0,
            quantity: Number(item.proquantity) || 1, // ensure number
            inventory: variant ? Number(variant.inventory) : 0 // ensure number
          };
        });

        setCartItems(items);
      }
    } catch (err) {
      console.error("Fetch error:", err);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to fetch cart data', width: 600, padding:'3em' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCartAndInventory(); }, []);

  // Handle quantity change with numbers
  const handleEditQuantity = (id, delta) => {
    setCartItems(prev =>
      prev.map(item => {
        if (item.id !== id) return item;

        let currentQty = Number(item.quantity) || 0;
        let newQuantity = currentQty + delta;

        if (newQuantity < 1) {
          Swal.fire({
            icon: "info",
            title: "Invalid Quantity",
            text: "Quantity cannot be less than 1.",
            width: 600,
            padding: '3em',
          });
          newQuantity = 1;
        }

        if (newQuantity > item.inventory) {
          Swal.fire({
            icon: "info",
            title: "Stock Limit",
            text: `Only ${item.inventory} items available in stock.`,
            width: 600,
            padding: '3em',
          });
          newQuantity = item.inventory;
        }

        return { ...item, quantity: newQuantity };
      })
    );
  };

  // Remove item
  const handleRemoveClick = async (cartId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to remove this item from cart?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, remove it!",
      cancelButtonText: "Cancel",
      width: 600,
      padding: '3em',
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`https://dharajwebbackend.concentics.com/api/delete_cart/${cartId}`);
        setCartItems(prev => prev.filter(item => item.id !== cartId));
        Swal.fire({
          icon: "success",
          title: "Removed!",
          text: "Item removed from cart.",
          width: 600,
          padding:'3em',
          confirmButtonColor:"#185E37"
        });
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to delete item.",
          width: 600,
          padding:'3em'
        });
      }
    }
  };

  const total = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);

  if (loading) return <p className="text-center mt-5 fs-4">Loading cart...</p>;

  return (
    <div className="container" style={{ maxWidth: "1080px", paddingTop: "150px", paddingBottom: "180px" }}>
      {/* Header */}
      <div className="p-5 rounded shadow-sm mb-4" style={{ background: "white", border: "1px solid #eee" }}>
        <h4 className="fw-bold fs-xl mb-3">Check delivery time & services</h4>
        <div className="p-3 rounded" style={{ background: "#f8f9fa" }}>
          <h5 className="fw-bold fs-xl mb-1">⚡ Dharaj — Premium Marketplace</h5>
          <p className="text-muted fs-lg mb-0 p-3">
            Trusted by thousands. Fast delivery · Secure payment · Easy returns
          </p>
        </div>
      </div>

      {/* Cart Items */}
      <div className="row">
        <div className="col-lg-8">
          {cartItems.length === 0 ? (
            <p className="text-center fs-lg py-5">Your cart is empty!</p>
          ) : (
            cartItems.map(item => (
              <div key={item.id} className="cart-item d-flex justify-content-between align-items-center p-3 mb-3 rounded shadow-sm" style={{ background: "white", border: "1px solid #eee" }}>
                
                {/* Desktop layout */}
                <div className="d-none d-md-flex align-items-center w-100 justify-content-between gap-4">
                  <div className="d-flex align-items-center gap-4">
                    <img src={item.image} alt={item.name} style={{ width: "90px", height: "90px", borderRadius: "10px", objectFit: "cover" }} />
                    <div>
                      <h6 className="fw-bold fs-xl">{item.name}</h6>
                      <div className="d-flex align-items-center gap-2 mt-2">
                        <span className="text-muted fs-lg me-2">Quantity:</span>
                        <div className="d-flex align-items-center rounded-pill px-2 py-1 shadow-sm" style={{ border: "1px solid #ddd", width: "110px", justifyContent: "space-between", background: "#fff" }}>
                          <button className="btn btn-sm border-0" style={{ fontSize: "18px", padding: "0 8px" }} onClick={() => handleEditQuantity(item.id, -1)}>−</button>
                          <span style={{ fontSize: "16px", fontWeight: "600" }}>{item.quantity}</span>
                          <button className="btn btn-sm border-0" style={{ fontSize: "18px", padding: "0 8px" }} onClick={() => handleEditQuantity(item.id, +1)}>+</button>
                        </div>
                      </div>
                      <span className="text-muted fs-md">Available: {item.inventory} In Stock</span>
                    </div>
                  </div>
                  <div className="d-flex flex-column align-items-end">
                    <h6 className="fw-bold fs-lg">₹ {(item.price * item.quantity).toFixed(2)}</h6>
                    <button className="btn btn-outline-danger btn-sm mt-2" onClick={() => handleRemoveClick(item.id)}>Remove</button>
                  </div>
                </div>

                {/* Mobile layout */}
                <div className="d-flex d-md-none flex-row align-items-center justify-content-between w-100">
                  <img src={item.image} alt={item.name} className="cart-img" />
                  <div className="flex-grow-1 mx-3 d-flex flex-column justify-content-between">
                    <h6 className="fw-bold fs-lg mb-2">{item.name}</h6>
                    <div className="d-flex align-items-center gap-2 mt-1">
                      <span className="text-muted fs-md">Qty:</span>
                      <div className="d-flex align-items-center rounded-pill px-2 py-1 shadow-sm quantity-box">
                        <button className="btn border-0" onClick={() => handleEditQuantity(item.id, -1)}>−</button>
                        <span className="fw-semibold">{item.quantity}</span>
                        <button className="btn border-0" onClick={() => handleEditQuantity(item.id, +1)}>+</button>
                      </div>
                    </div>
                    <span className="text-muted fs-md">Available: {item.inventory} In Stock</span>
                  </div>
                  <div className="d-flex flex-column align-items-end">
                    <h6 className="fw-bold fs-md mb-2">₹ {(item.price * item.quantity).toFixed(2)}</h6>
                    <button className="btn btn-outline-danger btn-sm" onClick={() => handleRemoveClick(item.id)}>Remove</button>
                  </div>
                </div>

              </div>
            ))
          )}
        </div>

        {/* Sidebar */}
        <div className="col-lg-4">
          <div className="p-4 rounded shadow-sm mb-3 bg-white border">
            <h5 className="fw-bold fs-xl mb-2">WHY DHARAJ?</h5>
            <div className="bg-light p-3 rounded">
              <p className="fw-bold fs-lg mb-1 p-2">Buying for a loved one?</p>
              <p className="text-muted fs-md mb-1">Your satisfaction is our top priority.</p>
              <a href="#" className="text-danger fw-bold fs-md">Need Help?</a>
            </div>
          </div>

          <div className="p-4 rounded shadow-sm bg-white border">
            <h5 className="fw-bold fs-lg mb-2">🛍 Shop with Confidence</h5>
            <ul className="list-unstyled fs-lg p-3 mb-0">
              <li>✔ Secure Payments</li>
              <li>✔ Authentic Products</li>
              <li>✔ Easy Returns</li>
              <li>✔ Fast Shipping</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="shadow-lg border-top fixed-bottom bg-white p-4" style={{ backdropFilter: "blur(15px)", zIndex: 999 }}>
        <div className="container d-flex flex-column flex-md-row justify-content-between align-items-center py-3" style={{ maxWidth: "1080px" }}>
          <div className="text-center text-md-start mb-2 mb-md-0">
            <h5 className="fw-bold fs-xl mb-1">PRICE DETAILS ({cartItems.length} Items)</h5>
            <h4 className="fw-bold text-success fs-xl">TOTAL PRICE : ₹ {total.toFixed(2)}</h4>
          </div>
          <button
            className="btn px-4 py-2 fw-semibold fs-xl"
            style={{ background: "#185E37", color: "white", borderRadius: "50px" }}
            onClick={async () => {
              if(cartItems.length === 0){
                Swal.fire({ icon: "info", title: "Cart Empty", text: "Please add items to checkout.", width:600, padding:'3em', confirmButtonColor:"#185E37" });
                return;
              }
              const result = await Swal.fire({
                title: "Proceed to Checkout?",
                html: `<p>Total Items: <b>${cartItems.length}</b></p><p>Total Price: <b>₹ ${total.toFixed(2)}</b></p>`,
                icon: "info",
                showCancelButton: true,
                confirmButtonText: "Yes, checkout",
                cancelButtonText: "Cancel",
                width: 600,
                padding: '3em',
                confirmButtonColor: "#185E37",
                cancelButtonColor: "#d33",
              });
              if (result.isConfirmed) navigate("/CheckOut/:status");
            }}
          >Proceed to Checkout</button>
        </div>
      </div>

      <style>{`
        .cart-img { width: 90px; height: 90px; border-radius: 10px; object-fit: cover; }
        .quantity-box button { font-size: 16px; padding: 0 6px; }
        .quantity-box span { width: 24px; text-align: center; }
        .fs-xl { font-size: 1.8rem; }
        .fs-lg { font-size: 1.4rem; }
        .fs-md { font-size: 1.1rem; }
        .fs-sm { font-size: 0.95rem; }

        @media(max-width: 768px) {
          .fs-xl { font-size: 1.5rem; }
          .fs-lg { font-size: 1.2rem; }
          .fs-md { font-size: 1rem; }
          .fs-sm { font-size: 0.9rem; }
          .cart-img { width: 70px; height: 70px; }
        }
      `}</style>
    </div>
  );
};

export default CartPage;
